/**
 * Pure, testable helpers for the read-only web ItineraryPanel: timezone-aware
 * day bucketing, the day list, the next game, and deep-link focus resolution
 * over a tournament's games. Mirrors the mobile itinerary-home helpers so the
 * two platforms bucket a game on the same local day.
 */

import type { TournamentGameDto } from "../../../../types/tournament-games";
import { dateTimeFormatter } from "../../../../utils/dateTimeParts";

/** YYYY-MM-DD in the game's own timezone (UTC fallback on a bad zone / instant). */
export const gameDayKey = (startsAtUtc: string, timeZone: string): string => {
  const d = new Date(startsAtUtc);
  if (Number.isNaN(d.getTime())) return "";
  // Cached: this runs once per game, on the itinerary and for every printout row.
  const fmt = (tz: string) =>
    dateTimeFormatter("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  try {
    return fmt(timeZone || "UTC");
  } catch {
    return fmt("UTC");
  }
};

export interface DayOption {
  /** YYYY-MM-DD in the game timezone; the stable selection key. */
  key: string;
  /** Short label, e.g. "Sat, Jul 5". */
  label: string;
}

/** Stable start-time ordering; ties fall back to the API's sortOrder. */
const byStart = (a: TournamentGameDto, b: TournamentGameDto): number => {
  const at = new Date(a.startsAtUtc).getTime();
  const bt = new Date(b.startsAtUtc).getTime();
  if (at !== bt) return at - bt;
  return a.sortOrder - b.sortOrder;
};

/** Distinct, date-sorted days present in the games, with tz-aware short labels. */
export const distinctDayKeys = (
  games: readonly TournamentGameDto[],
): DayOption[] => {
  const byKey = new Map<string, DayOption>();
  for (const g of games) {
    const key = gameDayKey(g.startsAtUtc, g.timeZone);
    if (!key || byKey.has(key)) continue;
    const d = new Date(g.startsAtUtc);
    const label = (() => {
      const opts: Intl.DateTimeFormatOptions = {
        weekday: "short",
        month: "short",
        day: "numeric",
      };
      try {
        return new Intl.DateTimeFormat("en-US", {
          ...opts,
          timeZone: g.timeZone || "UTC",
        }).format(d);
      } catch {
        return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: "UTC" }).format(d);
      }
    })();
    byKey.set(key, { key, label });
  }
  return [...byKey.values()].sort((a, b) => a.key.localeCompare(b.key));
};

/** Games on a given day (tz-aware), sorted by start time then sortOrder. */
export const gamesOnDay = (
  games: readonly TournamentGameDto[],
  dayKey: string,
): TournamentGameDto[] =>
  games
    .filter((g) => gameDayKey(g.startsAtUtc, g.timeZone) === dayKey)
    .sort(byStart);

/** The next game at/after `nowMs` (earliest start), or null if none remain. */
export const nextGame = (
  games: readonly TournamentGameDto[],
  nowMs: number,
): TournamentGameDto | null => {
  for (const g of [...games].sort(byStart)) {
    if (new Date(g.startsAtUtc).getTime() >= nowMs) return g;
  }
  return null;
};

export type ItineraryFilter = "mine" | "all";

/**
 * Which filter contains the focused game: 'mine' when the caller is covering it
 * (server-derived `isAssignedToMe`), otherwise 'all'. Null when the id is absent
 * (deleted/merged since the calendar loaded).
 */
export const tabForFocus = (
  games: readonly TournamentGameDto[],
  gameId: string,
): ItineraryFilter | null => {
  const g = games.find((x) => x.id === gameId);
  if (!g) return null;
  return g.isAssignedToMe ? "mine" : "all";
};

/** The focused game's day key, or null if the id is absent. */
export const dayForFocus = (
  games: readonly TournamentGameDto[],
  gameId: string,
): string | null => {
  const g = games.find((x) => x.id === gameId);
  return g ? gameDayKey(g.startsAtUtc, g.timeZone) : null;
};

/**
 * Which day to select by default: today if it has games, otherwise the first
 * upcoming day, otherwise the first day. Null when there are no days.
 */
export const defaultDayKey = (
  days: readonly DayOption[],
  todayKey: string,
): string | null => {
  if (days.length === 0) return null;
  const today = days.find((d) => d.key === todayKey);
  if (today) return today.key;
  const upcoming = days.find((d) => d.key >= todayKey);
  return (upcoming ?? days[0]).key;
};
